import RibbonPlugin from '../plugin/RibbonPlugin';
import RibbonViewState from '../store/schema/RibbonViewState';
import setIsContextualFormatBarShown from './setIsContextualFormatBarShown';
import {
    FormatState,
    PendableFormatState,
    ElementBasedFormatState,
    StyleBasedFormatState,
} from 'roosterjs-editor-types';
import { action } from 'satcheljs/lib/legacy';
import { getElementBasedFormatState, getPendableFormatState } from 'roosterjs-editor-api';

const FIRSTFONT_REGEX: RegExp = /^['"]?([^'",]+)/i;

// The interval to pull ribbon state
const RIBBON_STATE_POLL_INTERVAL: number = 500;

let ribbonStateJobId: number;

/**
 * First class states are the format states directly showing in ribbon, such as B/I/U/bullet/...
 * These states need to be updated after user changed cursor position or changed format
 */
export const updateFirstClassState = action('updateRibbonFormatState')(
    function updateRibbonFormatState(
        formatViewState: FormatState,
        pendingState: PendableFormatState,
        elementBasedState: ElementBasedFormatState,
        canUndo: boolean,
        canRedo: boolean
    ) {
        formatViewState.isBold = pendingState.isBold;
        formatViewState.isItalic = pendingState.isItalic;
        formatViewState.isUnderline = pendingState.isUnderline;
        formatViewState.isBullet = elementBasedState.isBullet;
        formatViewState.isNumbering = elementBasedState.isNumbering;
        formatViewState.isStrikeThrough = pendingState.isStrikeThrough;
        formatViewState.isSubscript = pendingState.isSubscript;
        formatViewState.isSuperscript = pendingState.isSuperscript;
        formatViewState.canUnlink = elementBasedState.canUnlink;
        formatViewState.canAddImageAltText = elementBasedState.canAddImageAltText;
        formatViewState.canUndo = canUndo;
        formatViewState.canRedo = canRedo;
        formatViewState.isBlockQuote = elementBasedState.isBlockQuote;
    }
);

/**
 * Second class states are the format states showing in drop down menu, such as font family, font size, colors, ...
 * These states only need to be updated when user click a button to show dropdown.
 */
export const updateSecondClassState = action('updateRibbonFormatState')(
    function updateRibbonFormatState(
        formatViewState: FormatState,
        styleBasedState: StyleBasedFormatState
    ) {
        // Check font name to ensure it matches the format we have as in FONT_NAME_LIST
        // The returned fontName could be a list of serveral fonts, i.e. Calibri, Arial, Helvetica, sans-serif
        // Let's only take the first one from the list
        if (styleBasedState.fontName) {
            // FIRSTFONT_REGEX is a group regex. The result at index 1 is the font we're after
            let matches: string[] = styleBasedState.fontName.match(FIRSTFONT_REGEX);
            formatViewState.fontName = matches && matches.length == 2 ? matches[1] : '';
        } else {
            formatViewState.fontName = '';
        }

        // Check font size to ensure it matches the format we have in FONT_SIZE_LIST
        let fs: number = parseInt(styleBasedState.fontSize);
        formatViewState.fontSize = isNaN(fs) ? '' : fs.toString();
        formatViewState.backgroundColor = styleBasedState.backgroundColor;
        formatViewState.textColor = styleBasedState.textColor;
    }
);

export default function updateRibbonState(
    plugin: RibbonPlugin,
    viewState: RibbonViewState,
    updateContextualFormatBar: boolean,
    delay: boolean
) {
    if (ribbonStateJobId) {
        window.clearTimeout(ribbonStateJobId);
    }

    let func = async () => {
        ribbonStateJobId = null;
        let editor = plugin.getEditor();
        let pendableState = editor && getPendableFormatState(editor.getDocument());
        let elementBasedState = editor && getElementBasedFormatState(editor);
        if (pendableState && elementBasedState) {
            elementBasedState.canAddImageAltText = !!(await plugin.getImageSelectedByPlugin());
            updateFirstClassState(
                viewState.formatViewState,
                pendableState,
                elementBasedState,
                editor.canUndo(),
                editor.canRedo()
            );
        }

        // Need to check editor.isDisposed() since we have async operation above and editor might be disposed during this time
        if (updateContextualFormatBar && editor && !editor.isDisposed()) {
            let range = editor.hasFocus() ? editor.getSelectionRange() : null;
            let hasTextSelection = range ? !range.collapsed : false;
            if (hasTextSelection && !viewState.isContextualFormatBarShown) {
                let rect = editor.getCursorRect();
                if (rect) {
                    // Rect.left and Rect.right are the same, so no need to consider RTL
                    setIsContextualFormatBarShown(viewState, hasTextSelection, rect.left, rect.top);
                }
            } else if (!hasTextSelection && viewState.isContextualFormatBarShown) {
                setIsContextualFormatBarShown(viewState, hasTextSelection);
            }
        }
    };

    if (delay) {
        ribbonStateJobId = window.setTimeout(func, RIBBON_STATE_POLL_INTERVAL);
    } else {
        func();
    }
}
